'use client';

import {
  useState,
  ReactNode,
  forwardRef,
  useImperativeHandle,
  useEffect,
  useContext,
  useRef,
} from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { FaCheck, FaRotateLeft, FaLock } from 'react-icons/fa6';
import { Button, Tooltip } from '@heroui/react';
import { InternalStepValidationContext } from '../context/InternalStepValidationContext';
import { useScheduler } from '../SchedulerContext';
import {
  SchedulerStepName,
  useTrackSchedulerScreen,
} from '../hooks/useTrackSchedulerScreen';

export type InternalStep = {
  /** Stable snake_case id, reported as `screen` on `scheduler_step_viewed`. */
  id: string;
  label: string;
  content: ReactNode;
};

type InternalStepperProps = {
  steps: InternalStep[];
  onStartOver?: () => void;
  initialStep?: number; // NEW: Allow parent to specify starting step based on context
  /** Main scheduler step these sub-steps belong to; when set, each sub-step view is tracked. */
  trackingStep?: SchedulerStepName;
};

export type InternalStepperRef = {
  setActiveStep: (step: number) => void;
};

const InternalStepper = forwardRef<InternalStepperRef, InternalStepperProps>(
  ({ steps, onStartOver, initialStep = 0, trackingStep }, ref) => {
    const [activeStep, setActiveStepState] = useState(initialStep);
    const [direction, setDirection] = useState<'up' | 'down'>('down');
    const hasInitialized = useRef(false);
    const lastInitialStepRef = useRef(initialStep);
    const lastMarkedStepsRef = useRef<Set<number>>(new Set()); // Track which steps we've already marked
    const userHasNavigatedRef = useRef(false); // Track if user has manually navigated

    // Get company styles for branding
    const { companyStyles } = useScheduler();

    // Use validation context - must be called unconditionally (React hooks rule)
    // Use useContext directly instead of the hook that throws an error
    const validationContext = useContext(InternalStepValidationContext);

    const activeStepId = steps[activeStep]?.id;
    useTrackSchedulerScreen(
      trackingStep && activeStepId
        ? { step: trackingStep, screen: activeStepId }
        : null
    );

    // Consolidate: Mark steps as visited and update active step when initialStep changes
    // This handles both initial mount and updates when draft loads after mount
    // CRITICAL: Only auto-navigate on initialization or draft resume, NOT when data updates during normal flow
    useEffect(() => {
      // Only mark steps if initialStep actually changed OR we're initializing - prevents infinite loop
      const stepChanged = initialStep !== lastInitialStepRef.current;
      const isInitializing = !hasInitialized.current;

      // Mark all steps from 0 to initialStep as visited
      // This allows proper navigation when resuming from a draft
      // Only mark steps that haven't been marked yet for this initialStep value
      if (validationContext && (stepChanged || isInitializing)) {
        // Clear previous marks when step changes (but not on initial mount)
        if (stepChanged && !isInitializing) {
          lastMarkedStepsRef.current.clear();
        }

        for (let i = 0; i <= initialStep; i++) {
          // Only mark if not already visited AND not already marked in this effect run
          if (
            !validationContext.isStepVisited(i) &&
            !lastMarkedStepsRef.current.has(i)
          ) {
            validationContext.markStepVisited(i);
            lastMarkedStepsRef.current.add(i);
          }
        }
      }

      // Reset user navigation flag when initializing (fresh draft load)
      if (isInitializing) {
        userHasNavigatedRef.current = false;
      }

      // Update active step ONLY if:
      // 1. We're initializing (first mount or draft resume), OR
      // 2. initialStep changed AND user hasn't manually navigated yet
      // This prevents auto-navigation when data updates (like Zillow loading) after user has navigated
      // CRITICAL: Always allow navigation to step 0 (starting over)
      const shouldUpdateStep =
        (isInitializing && initialStep >= 0) ||
        (stepChanged && initialStep >= 0 && !userHasNavigatedRef.current);

      if (shouldUpdateStep) {
        // console.log('[InternalStepper] Setting initial step to:', initialStep, {
        //   isInitializing,
        //   stepChanged,
        //   userHasNavigated: userHasNavigatedRef.current,
        // });
        // Directly set the step - steps are already marked as visited above
        // Step 0 is always allowed (handled in canNavigateToStep)
        setActiveStepState(initialStep);
        hasInitialized.current = true;
        lastInitialStepRef.current = initialStep;
        // After initial step is set, mark as "user navigated" to prevent
        // auto-advancing when data changes during normal form use
        userHasNavigatedRef.current = true;
      } else if (isInitializing) {
        // Mark as initialized even if initialStep is 0
        hasInitialized.current = true;
        // Ensure step 0 is marked as visited when initializing at step 0
        if (initialStep === 0 && validationContext) {
          validationContext.markStepVisited(0);
        }
      }
    }, [initialStep]); // REMOVED validationContext from dependencies to prevent loop

    // Strict navigation: check if we can navigate to a step
    const canNavigateToStep = (targetStep: number): boolean => {
      // Always allow current step
      if (targetStep === activeStep) return true;

      // Always allow navigation to step 0 (starting step - always accessible)
      if (targetStep === 0) return true;

      // Always allow backward navigation (users can review/edit completed steps)
      if (targetStep < activeStep) return true;

      // STRICT MODE: For forward navigation, ALL previous steps must be valid AND visited
      if (!validationContext) {
        return true; // Allow navigation if no validation context (shouldn't happen)
      }

      // For forward navigation: check ALL steps from 0 to target step
      if (targetStep > activeStep) {
        for (let i = 0; i < targetStep; i++) {
          const stepValid = validationContext.isStepValid(i);
          const stepVisited = validationContext.isStepVisited(i);

          // For forward navigation, a step must be BOTH valid AND visited
          // This ensures users can't skip steps by navigating directly to later steps
          if (!stepValid || !stepVisited) {
            return false;
          }
        }
      }

      return true;
    };

    const setActiveStep = (index: number) => {
      if (index < 0 || index >= steps.length) return;

      // Check validation before allowing navigation (enforces strict mode)
      if (!canNavigateToStep(index)) {
        // console.log('[InternalStepper] Navigation blocked to step', index);
        // When resuming a draft or initializing, we may need to mark previous steps as visited
        // If step is valid but not visited, mark it and try again
        if (validationContext && index > activeStep) {
          // Mark all previous steps as visited if they're valid
          let allPreviousValid = true;
          for (let i = 0; i < index; i++) {
            const stepValid = validationContext.isStepValid(i);
            if (stepValid && !validationContext.isStepVisited(i)) {
              // console.log(
              //   '[InternalStepper] Auto-marking step',
              //   i,
              //   'as visited (draft resume/init)'
              // );
              validationContext.markStepVisited(i);
            }
            // Track if all previous steps are valid
            if (!stepValid) {
              allPreviousValid = false;
            }
          }
          // If all previous steps are valid (even if not all visited yet), allow navigation
          // This handles the case where we're initializing and steps are being marked
          if (allPreviousValid) {
            // console.log(
            //   '[InternalStepper] All previous steps valid, allowing navigation'
            // );
            // Mark the target step as visited too
            validationContext.markStepVisited(index);
          } else {
            // Try navigation again after marking steps
            if (!canNavigateToStep(index)) {
              console.log(
                '[InternalStepper] Navigation still blocked after marking steps'
              );
              return;
            }
          }
        } else {
          return; // Block navigation if validation fails
        }
      }

      // Mark step as visited when navigating to it
      if (validationContext) {
        validationContext.markStepVisited(index);
      }

      // Track that user has manually navigated (prevents auto-navigation on data updates)
      if (index !== activeStep) {
        userHasNavigatedRef.current = true;
      }

      setDirection(index > activeStep ? 'down' : 'up');
      setActiveStepState(index);
    };

    // Wrap onStartOver to reset visited steps
    const handleStartOver = () => {
      // Reset visited steps when starting over
      if (validationContext) {
        validationContext.resetVisitedSteps();
        // Mark step 0 as visited so it's accessible
        validationContext.markStepVisited(0);
      }
      // Reset user navigation flag
      userHasNavigatedRef.current = false;
      // Reset initialization ref to allow re-initialization
      hasInitialized.current = false;
      // Reset last marked steps
      lastMarkedStepsRef.current.clear();
      // Reset to step 0
      setActiveStepState(0);
      // Call the original onStartOver handler
      if (onStartOver) {
        onStartOver();
      }
    };

    useImperativeHandle(ref, () => ({
      setActiveStep,
    }));

    const handleStepClick = (index: number) => {
      if (index === activeStep) return;
      setActiveStep(index); // This will check validation
    };

    const variants = {
      enter: (direction: string) => ({
        y: direction === 'down' ? '100%' : '-100%',
        opacity: 0,
      }),
      center: {
        y: 0,
        opacity: 1,
      },
      exit: (direction: string) => ({
        y: direction === 'down' ? '-100%' : '100%',
        opacity: 0,
      }),
    };

    // Split steps for mobile layout
    const completedAndCurrent = steps.slice(0, activeStep + 1);
    const upcoming = steps.slice(activeStep + 1);

    // Helper to render step button
    const renderStepButton = (step: InternalStep, index: number) => {
      const isCurrent = activeStep === index;

      // Compute all validation state once to avoid multiple calls
      let isCompleted = false;
      let isValid = true;
      let canNavigate = true;
      let missingFields: string[] = [];

      if (validationContext) {
        // Get all validation data in one go
        const isVisited = validationContext.isStepVisited(index);
        isValid = validationContext.isStepValid(index);
        canNavigate = canNavigateToStep(index);
        missingFields = validationContext.getMissingFields(index);

        // Check if all previous steps are valid
        let allPreviousValid = true;
        for (let i = 0; i < index; i++) {
          if (!validationContext.isStepValid(i)) {
            allPreviousValid = false;
            break;
          }
        }

        // Step is completed only if: visited AND valid AND all previous steps are valid
        isCompleted = isVisited && isValid && allPreviousValid;
      } else {
        // Fallback to position-based logic if no validation context
        isCompleted = activeStep > index;
      }

      const buttonContent = (
        <button
          key={index}
          onClick={() => handleStepClick(index)}
          disabled={!canNavigate}
          className={`flex w-full items-center gap-3 rounded-lg p-3 text-left transition-all ${
            isCurrent
              ? 'text-white shadow-md'
              : isCompleted
              ? 'bg-green-50 text-success-700 hover:bg-green-100'
              : canNavigate
              ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              : 'cursor-not-allowed bg-gray-100 text-gray-400 opacity-60'
          } ${!canNavigate ? 'cursor-not-allowed' : 'cursor-pointer'}`}
          style={
            isCurrent
              ? { backgroundColor: companyStyles.companyColor || '#006FEE' }
              : undefined
          }
        >
          <div
            className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold ${
              isCurrent
                ? 'bg-white'
                : isCompleted
                ? 'bg-success text-white'
                : canNavigate
                ? 'bg-gray-300 text-gray-700'
                : 'bg-gray-200 text-gray-400'
            }`}
            style={
              isCurrent
                ? { color: companyStyles.companyColor || '#006FEE' }
                : undefined
            }
          >
            {isCompleted ? (
              <FaCheck size={12} />
            ) : !canNavigate ? (
              <FaLock size={10} />
            ) : (
              index + 1
            )}
          </div>
          <span className='font-medium'>{step.label}</span>
        </button>
      );

      // Show tooltip for locked steps
      if (!canNavigate && missingFields.length > 0) {
        return (
          <Tooltip
            key={index}
            content={
              <div className='max-w-xs'>
                <p className='mb-1 font-semibold'>Complete Required Fields:</p>
                <ul className='list-disc pl-4 text-xs'>
                  {missingFields.map((field, idx) => (
                    <li key={idx}>{field}</li>
                  ))}
                </ul>
              </div>
            }
            placement='right'
            delay={300}
          >
            {buttonContent}
          </Tooltip>
        );
      }

      return buttonContent;
    };

    return (
      <>
        {/* Desktop Layout */}
        <div className='hidden h-full gap-4 md:flex'>
          {/* Left Sidebar Menu - 1/4 */}
          <div className='w-1/4'>
            <nav className='sticky top-4 space-y-2'>
              {steps.map((step, index) => renderStepButton(step, index))}
              {onStartOver && (
                <Button
                  onPress={handleStartOver}
                  variant='flat'
                  startContent={<FaRotateLeft />}
                  color='warning'
                  className='w-full'
                >
                  Start Over
                </Button>
              )}
            </nav>
          </div>

          {/* Right Content Area - 3/4 */}
          <div className='w-3/4'>
            <AnimatePresence mode='wait' custom={direction}>
              <motion.div
                key={activeStep}
                custom={direction}
                variants={variants}
                initial='enter'
                animate='center'
                exit='exit'
                transition={{ duration: 0.3, ease: 'easeInOut' }}
                className='h-full'
              >
                {steps[activeStep]?.content}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* Mobile Layout */}
        <div className='flex h-full flex-col gap-4 md:hidden'>
          {/* Completed and Current Steps - Above Content */}
          <nav className='space-y-2'>
            {completedAndCurrent.map((step, idx) =>
              renderStepButton(step, idx)
            )}
          </nav>

          {/* Content Area */}
          <div className='flex-1'>
            <AnimatePresence mode='wait' custom={direction}>
              <motion.div
                key={activeStep}
                custom={direction}
                variants={variants}
                initial='enter'
                animate='center'
                exit='exit'
                transition={{ duration: 0.3, ease: 'easeInOut' }}
                className='h-full'
              >
                {steps[activeStep]?.content}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Upcoming Steps - Below Content */}
          {upcoming.length > 0 && (
            <nav className='space-y-2'>
              {upcoming.map((step, idx) =>
                renderStepButton(step, activeStep + 1 + idx)
              )}
            </nav>
          )}

          {/* Start Over Button */}
          {onStartOver && (
            <div className='flex justify-center pt-2'>
              <Button
                onPress={handleStartOver}
                variant='flat'
                startContent={<FaRotateLeft />}
                color='warning'
              >
                Start Over
              </Button>
            </div>
          )}
        </div>
      </>
    );
  }
);

InternalStepper.displayName = 'InternalStepper';

export default InternalStepper;
